import { useState, useEffect } from 'react';
import {
  Box,
  Grid,
  Typography,
  CircularProgress,
  Alert,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  Tooltip,
  Checkbox,
  Button
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { tokens } from '../../theme';
import LazyCardImage from './LazyCardImage';
import AuthService from '../../services/AuthService';
import axios from 'axios';
import SellIcon from '@mui/icons-material/Sell';
import CheckBoxIcon from '@mui/icons-material/CheckBox';
import CheckBoxOutlineBlankIcon from '@mui/icons-material/CheckBoxOutlineBlank';
import { useListing } from '../../contexts/ListingContext';
import { 
  getElementInfoByColor,
  getRarityInfoByValue,
  getCardTypeInfoByValue
} from "../../constants/filterConstants";

/**
 * MyCardsTab - Shows all copies of a specific card that the user owns
 * This tab is displayed in the CardDetailsDialog when viewing "My Cards"
 */
const MyCardsTab = ({ card, details, isCollection = false, userCards = [], selectedCardLevel = null }) => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  
  const [myCards, setMyCards] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [username, setUsername] = useState(null); // Dynamischer Username aus AuthService
  const [selectedCards, setSelectedCards] = useState(new Set());
  
  // Listing context
  const { addToListing, loading: listingLoading } = useListing();

  // AuthService subscription to get current user
  useEffect(() => {
    const unsubscribe = AuthService.subscribe(({ isLoggedIn, currentUser }) => {
      if (isLoggedIn && currentUser && currentUser.username) {
        setUsername(currentUser.username);
      } else {
        setUsername(null);
        setError('Please log in to view your cards');
      }
    });

    return () => unsubscribe();
  }, []);

  // Fetch user's cards for this specific card_detail_id
  useEffect(() => {
    const fetchUserCards = async () => {
      if (!card || !username) return;
      
      // If we're in collection mode and have userCards passed in, use those
      if (isCollection && userCards && userCards.length > 0) {
        const matchingCards = userCards.filter(userCard => {
          // Must match card_detail_id
          if (userCard.card_detail_id !== card.card_detail_id) return false;
          
          // Must match foil properties
          const userCardGold = Boolean(userCard.gold);
          const userCardFoil = userCard.foil || 0;
          const currentCardGold = Boolean(card.gold);
          const currentCardFoil = card.foil || 0;
          
          return userCardGold === currentCardGold && userCardFoil === currentCardFoil;
        });
        
        // Sort by level descending, then by foil/gold
        const sortedCards = matchingCards.sort((a, b) => {
          if (b.level !== a.level) return b.level - a.level;
          if (b.gold !== a.gold) return b.gold - a.gold;
          return (b.foil || 0) - (a.foil || 0);
        });
        
        setMyCards(sortedCards);
        setLoading(false);
        return;
      }
      
      // Otherwise fetch from API
      setLoading(true);
      setError(null);
      
      try {
        const response = await axios.get(`https://api.splinterlands.com/cards/collection/${username}`);
        const allCards = Array.isArray(response.data) ? response.data : response.data?.cards || [];
        
        // Filter cards that match the current card_detail_id and foil properties
        const matchingCards = allCards.filter(userCard => {
          // Must match card_detail_id
          if (userCard.card_detail_id !== card.card_detail_id) return false;
          
          // Must match foil properties
          const userCardGold = Boolean(userCard.gold);
          const userCardFoil = userCard.foil || 0;
          const currentCardGold = Boolean(card.gold);
          const currentCardFoil = card.foil || 0;
          
          return userCardGold === currentCardGold && userCardFoil === currentCardFoil;
        });
        
        // Sort by level descending, then by foil/gold
        const sortedCards = matchingCards.sort((a, b) => {
          // First by level (highest first)
          if (b.level !== a.level) return b.level - a.level;
          // Then by gold (gold first)
          if (b.gold !== a.gold) return b.gold - a.gold;
          // Then by foil (highest first)
          return (b.foil || 0) - (a.foil || 0);
        });
        
        setMyCards(sortedCards);
      } catch (err) {
        console.error('Error fetching user cards:', err);
        setError('Failed to load your cards');
      } finally {
        setLoading(false);
      }
    };

    fetchUserCards();
  }, [card, username, isCollection, userCards]);

  // Comprehensive helper functions for card state validation based on Splinterlands API

  // Helper function to check if a card is on the market (for sale)
  const isCardOnMarket = (card) => {
    return (card.market_id !== null && card.market_id !== undefined) ||
           (card.market_listing_status !== null && card.market_listing_status !== undefined) ||
           (card.market_listing_type !== null && card.market_listing_type !== undefined) ||
           (card.market_listing_id !== null && card.market_listing_id !== undefined) ||
           (card.sell_trx_id !== null && card.sell_trx_id !== undefined) ||
           (card.listing_id !== null && card.listing_id !== undefined) ||
           (card.buy_price !== null && card.buy_price !== undefined) ||
           (card.market && card.market.length > 0) ||
           (card.for_sale === true) ||
           (card.is_for_sale === true);
  };

  // Helper function to check if a card is rented out by the owner
  const isCardRentedOut = (card) => {
    return (card.rental_type !== null && card.rental_type !== undefined) ||
           (card.rental_days !== null && card.rental_days !== undefined) ||
           (card.rental_date !== null && card.rental_date !== undefined) ||
           (card.next_rental_payment !== null && card.next_rental_payment !== undefined);
  };

  // Helper function to check if a card is rented to the current user
  const isCardRentedToUser = (card) => {
    // This requires checking if the card owner is different from the current user
    // and there's active rental data
    return card.player !== username && isCardRentedOut(card);
  };

  // Helper function to check if a card is delegated out
  const isCardDelegatedOut = (card) => {
    // Check if the card has been delegated to another player
    const delegatedTo = card.delegated_to;
    
    // Card is delegated out if it has a valid delegated_to username
    return (delegatedTo && 
            delegatedTo !== null && 
            delegatedTo !== undefined && 
            delegatedTo !== '' && 
            delegatedTo.trim() !== '');
  };

  // Helper function to check if a card is delegated to the current user
  const isCardDelegatedToUser = (card) => {
    // A card is delegated to the current user if:
    // 1. The card owner (player) is different from current user
    // 2. AND the current user is viewing it in their collection (meaning it's delegated to them)
    // 3. AND it has delegation info pointing to the current user
    
    // If we see a card in our collection that belongs to someone else, it's delegated to us
    return card.player && 
           card.player !== username && 
           card.player !== '' && 
           card.delegated_to === username;
  };

  // Helper function to check if a card is soulbound (cannot be traded)
  const isCardSoulbound = (card) => {
    const edition = parseInt(card.edition);
    
    // Check for explicit soulbound markers first
    if (card.soulbound === true || card.is_soulbound === true) return true;
    
    // Edition-based soulbound detection
    // Gladius (6), Soulbound (10), Soulbound Rebellion (13) editions
    if (edition === 6 || edition === 10 || edition === 13) {
      // For these editions, check if there are unbound BCX available
      const unboundBcx = parseInt(card.bcx_unbound) || 0;
      const totalBcx = parseInt(card.bcx) || 1;
      
      // If bcx_unbound exists and is 0, then it's fully soulbound
      if (card.bcx_unbound !== undefined && card.bcx_unbound !== null && unboundBcx === 0 && totalBcx > 0) {
        return true; // Fully soulbound
      }
    }
    
    // For Reward edition (3), do NOT use bcx_unbound field as many tradeable cards have bcx_unbound: 0
    // Only consider explicit soulbound markers for Reward cards
    if (edition === 3) {
      // Only flag as soulbound if there are explicit soulbound indicators
      // Most Reward cards are tradeable even with bcx_unbound: 0
      return card.soulbound === true || card.is_soulbound === true;
    }
    
    return false;
  };

  // Helper function to check if a card is locked (in staking, battles, etc.)
  const isCardLocked = (card) => {
    return (card.lock_days !== null && card.lock_days !== undefined && card.lock_days > 0) ||
           (card.locked === true) ||
           (card.is_locked === true) ||
           (card.lock_date !== null && card.lock_date !== undefined);
  };

  // Helper function to check if a card is unlocking
  const isCardUnlocking = (card) => {
    // Check if card has lock information but is in process of unlocking
    return (card.unlock_date !== null && card.unlock_date !== undefined) ||
           (card.unlocking === true) ||
           (card.is_unlocking === true);
  };

  // Helper function to check if a card is on land
  const isCardOnLand = (card) => {
    return (card.land_id !== null && card.land_id !== undefined) ||
           (card.on_land === true) ||
           (card.land_plot !== null && card.land_plot !== undefined) ||
           (card.stake_plot !== null && card.stake_plot !== undefined) ||
           (card.stake_region !== null && card.stake_region !== undefined) ||
           (card.stake_ref_uid !== null && card.stake_ref_uid !== undefined);
  };

  // Helper function to check if a card is in sets
  const isCardInSets = (card) => {
    return (card.set_id !== null && card.set_id !== undefined) ||
           (card.in_set === true) ||
           (card.team_id !== null && card.team_id !== undefined);
  };

  // Helper function to check if a card is on wagons
  const isCardOnWagons = (card) => {
    return (card.wagon_id !== null && card.wagon_id !== undefined) ||
           (card.wagon_uid !== null && card.wagon_uid !== undefined) ||
           (card.on_wagon === true);
  };

  // Helper function to check if a card is on a different blockchain
  const isCardOnDifferentBlockchain = (card) => {
    return (card.blockchain !== null && card.blockchain !== undefined && card.blockchain !== 'hive') ||
           (card.ethereum === true) ||
           (card.wax === true);
  };

  // Helper function to get all reasons why a card cannot be traded
  const getCardRestrictions = (card) => {
    const restrictions = [];
    
    if (isCardOnMarket(card)) restrictions.push('On Market');
    if (isCardRentedOut(card)) restrictions.push('Rented Out');
    if (isCardRentedToUser(card)) restrictions.push('Rented to Me');
    if (isCardDelegatedOut(card)) restrictions.push('Delegated Out'); 
    if (isCardDelegatedToUser(card)) restrictions.push('Delegated to Me');
    if (isCardSoulbound(card)) restrictions.push('Soulbound');
    if (isCardLocked(card)) restrictions.push('Locked');
    if (isCardUnlocking(card)) restrictions.push('Unlocking');
    if (isCardOnLand(card)) restrictions.push('On Land');
    if (isCardInSets(card)) restrictions.push('In Sets');
    if (isCardOnWagons(card)) restrictions.push('On Wagons');
    if (isCardOnDifferentBlockchain(card)) restrictions.push('Different Blockchain');
    
    return restrictions;
  };

  // Helper function to check if a card can be traded (truly tradeable)
  const isCardTradeable = (card) => {
    const restrictions = getCardRestrictions(card);
    return restrictions.length === 0;
  };

  // Helper function to check if a card can be listed (tradeable and not already on market)
  const canListCard = (card) => {
    return isCardTradeable(card) && !isCardOnMarket(card);
  };


  // Helper function to get foil type name
  const getFoilTypeName = (foil) => {
    switch (foil) {
      case 1: return 'Foil';
      case 2: return 'Gold';
      case 3: return 'Foil Gold';
      case 4: return 'Black';
      default: return 'Regular';
    }
  };

  // Handle card selection for listing with shift-click support
  const [lastSelectedIndex, setLastSelectedIndex] = useState(null);
  
  const handleCardSelect = (cardUid, isSelected, index, event) => {
    console.log('handleCardSelect called:', { cardUid, isSelected, index });
    
    if (event?.shiftKey && lastSelectedIndex !== null) {
      // Shift-click: select range
      const start = Math.min(lastSelectedIndex, index);
      const end = Math.max(lastSelectedIndex, index);
      
      setSelectedCards(prev => {
        const newSet = new Set(prev);
        for (let i = start; i <= end; i++) {
          const card = myCards[i];
          if (card && canListCard(card)) { // Only select cards that can be listed
            newSet.add(card.uid);
          }
        }
        return newSet;
      });
    } else {
      // Normal click: select single card
      setSelectedCards(prev => {
        const newSet = new Set(prev);
        if (isSelected) {
          newSet.add(cardUid);
        } else {
          newSet.delete(cardUid);
        }
        return newSet;
      });
      setLastSelectedIndex(index);
    }
  };

  // Select all cards
  const handleSelectAll = () => {
    const listableCards = myCards.filter(card => canListCard(card));
    const listableCardUids = listableCards.map(card => card.uid);
    
    // Check if all listable cards are selected
    const allSelected = listableCardUids.length > 0 && 
                       listableCardUids.every(uid => selectedCards.has(uid));
    
    if (allSelected) {
      setSelectedCards(new Set());
    } else {
      setSelectedCards(new Set(listableCardUids));
    }
  };

  // Handle adding selected cards to listing
  const handleAddToListing = async () => {
    const cardsToList = myCards.filter(card => selectedCards.has(card.uid) && canListCard(card));
    
    if (cardsToList.length === 0) {
      // Check selected cards for restrictions and show detailed message
      const selectedCardsArray = myCards.filter(card => selectedCards.has(card.uid));
      let restrictionCounts = {};
      
      selectedCardsArray.forEach(card => {
        if (!canListCard(card)) {
          const restrictions = getCardRestrictions(card);
          restrictions.forEach(restriction => {
            restrictionCounts[restriction] = (restrictionCounts[restriction] || 0) + 1;
          });
        }
      });
      
      if (Object.keys(restrictionCounts).length > 0) {
        const restrictionMessages = Object.entries(restrictionCounts)
          .map(([restriction, count]) => `- ${count} card(s) ${restriction}`)
          .join('\n');
        
        alert(`Cannot list selected cards:\n${restrictionMessages}\n\nOnly fully tradeable cards that are not already on the market can be listed.`);
      } else {
        alert('No cards available for listing.');
      }
      return;
    }

    try {
      await addToListing(cardsToList, details);
      setSelectedCards(new Set()); // Clear selection
      
      // Show success message or notification here
      console.log(`Added ${cardsToList.length} cards to listing queue`);
    } catch (error) {
      console.error('Error adding cards to listing:', error);
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" p={4}>
        <CircularProgress sx={{ color: colors.greenAccent[400] }} />
        <Typography variant="h6" color={colors.grey[100]} ml={2}>
          Loading your cards...
        </Typography>
      </Box>
    );
  }

  if (error) {
    return (
      <Box p={3}>
        <Alert severity="error">{error}</Alert>
      </Box>
    );
  }

  if (myCards.length === 0) {
    return (
      <Box p={3}>
        <Alert severity="info">
          You don't own any copies of this card.
        </Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ pt: 3, px: 3 }}>
      <Grid container spacing={3}>
        {/* Card Display - same layout as other tabs */}
        <Grid item xs={12} md={3} lg={2.5}>
          <Box
            sx={{
              height: { xs: 300, md: 350 },
              position: "relative",
              borderRadius: "10px",
              overflow: "hidden",
              boxShadow: "0 4px 8px rgba(0,0,0,0.2)",
              border: `2px solid ${card?.gold ? "#DAA520" : colors.primary[400]}`,
              mb: 2,
              maxWidth: { xs: '100%', md: '100%' },
              mx: 'auto'
            }}
          >
            {card && details && (
              <LazyCardImage 
                card={card} 
                details={details} 
                showBaseImages={true}
                alt={details?.name || 'Card'}
                showArcanaEffect={details?.type === "Arcana"}
                customLevel={selectedCardLevel}
              />
            )}
          </Box>

          {/* Level-Anzeige für die aktuelle Kartenanzeige */}
          {selectedCardLevel && (
            <Box sx={{ mt: 1, textAlign: 'center' }}>
              <Chip
                label={`Showing Level ${selectedCardLevel}`}
                size="small"
                sx={{
                  bgcolor: colors.greenAccent[600],
                  color: 'white',
                  fontWeight: 'bold',
                  fontSize: '11px'
                }}
              />
            </Box>
          )}

          {/* Card Summary */}
          <Box sx={{ textAlign: 'center' }}>
            <Typography variant="h6" color={colors.grey[100]} gutterBottom>
              {details?.name || 'Unknown Card'}
            </Typography>
            
            {/* Kartenattribute horizontal angeordnet */}
            <Box display="flex" justifyContent="center" alignItems="center" gap={1} mb={2}>
              {(() => {
                const element = getElementInfoByColor(details?.color);
                return element && (
                  <Tooltip title={element.name}>
                    <Box
                      component="img"
                      src={element.activeIcon}
                      alt={element.name}
                      sx={{
                        width: 20,
                        height: 20,
                        objectFit: 'contain'
                      }}
                    />
                  </Tooltip>
                );
              })()}
              {(() => {
                const cardTypeInfo = getCardTypeInfoByValue(details?.type);
                return cardTypeInfo && (
                  <Tooltip title={cardTypeInfo.name}>
                    <Box
                      component="img"
                      src={cardTypeInfo.icon}
                      alt={cardTypeInfo.name}
                      sx={{
                        width: 20,
                        height: 20,
                        objectFit: 'contain'
                      }}
                    />
                  </Tooltip>
                );
              })()}
              {(() => {
                const rarityInfo = getRarityInfoByValue(details?.rarity);
                return rarityInfo && (
                  <Tooltip title={rarityInfo.name}>
                    <Box
                      component="img"
                      src={rarityInfo.icon}
                      alt={rarityInfo.name}
                      sx={{
                        width: 20,
                        height: 20,
                        objectFit: 'contain'
                      }}
                    />
                  </Tooltip>
                );
              })()}
            </Box>
            
            <Chip 
              label={`${myCards.length} copies owned`}
              sx={{ 
                backgroundColor: colors.greenAccent[600],
                color: colors.grey[100],
                fontWeight: 'bold'
              }}
            />
          </Box>
        </Grid>

        {/* User's Cards Table */}
        <Grid item xs={12} md={9} lg={9.5}>
          <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
            <Typography variant="h5" color={colors.grey[100]}>
              Your Collection ({myCards.length} cards)
            </Typography>
            
            {/* Listing Controls */}
            <Box display="flex" gap={2}>
              <Button
                variant="outlined"
                onClick={handleSelectAll}
                sx={{
                  color: colors.grey[100],
                  borderColor: colors.primary[300],
                  '&:hover': {
                    borderColor: colors.greenAccent[400],
                    backgroundColor: colors.primary[500]
                  }
                }}                >
                {(() => {
                  const listableCards = myCards.filter(card => canListCard(card));
                  const listableCardUids = listableCards.map(card => card.uid);
                  const allSelected = listableCardUids.length > 0 && 
                                     listableCardUids.every(uid => selectedCards.has(uid));
                  return allSelected ? 'Deselect All' : 'Select All';
                })()}
              </Button>
              
              {selectedCards.size > 0 && (
                <Button
                  variant="contained"
                  startIcon={<SellIcon />}
                  onClick={handleAddToListing}
                  disabled={listingLoading}
                  sx={{
                    backgroundColor: colors.greenAccent[600],
                    color: colors.grey[100],
                    '&:hover': {
                      backgroundColor: colors.greenAccent[700]
                    }
                  }}
                >
                  List {selectedCards.size} Card{selectedCards.size > 1 ? 's' : ''}
                </Button>
              )}
            </Box>
          </Box>
          
          <TableContainer 
            component={Paper} 
            sx={{ 
              backgroundColor: colors.primary[400],
              maxHeight: { 
                xs: 400, 
                md: 500, 
                lg: 600, 
                xl: 700 
              },
              overflow: 'auto'
            }}
          >
            <Table stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell sx={{ backgroundColor: colors.primary[300], color: colors.grey[100], fontWeight: 'bold' }}>
                    Select
                  </TableCell>
                  <TableCell sx={{ backgroundColor: colors.primary[300], color: colors.grey[100], fontWeight: 'bold' }}>
                    Level
                  </TableCell>
                  <TableCell sx={{ backgroundColor: colors.primary[300], color: colors.grey[100], fontWeight: 'bold' }}>
                    BCX
                  </TableCell>
                  <TableCell sx={{ backgroundColor: colors.primary[300], color: colors.grey[100], fontWeight: 'bold' }}>
                    CP
                  </TableCell>
                  <TableCell sx={{ backgroundColor: colors.primary[300], color: colors.grey[100], fontWeight: 'bold' }}>
                    Type
                  </TableCell>
                  <TableCell sx={{ backgroundColor: colors.primary[300], color: colors.grey[100], fontWeight: 'bold' }}>
                    Market Status
                  </TableCell>
                  <TableCell sx={{ backgroundColor: colors.primary[300], color: colors.grey[100], fontWeight: 'bold' }}>
                    Listing Price
                  </TableCell>
                  <TableCell sx={{ backgroundColor: colors.primary[300], color: colors.grey[100], fontWeight: 'bold' }}>
                    Card ID
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {myCards.map((userCard, index) => {
                  const isSelected = selectedCards.has(userCard.uid);
                  const isOnMarket = isCardOnMarket(userCard);
                  const tradeable = isCardTradeable(userCard);
                  const canList = canListCard(userCard);
                  
                  return (
                    <TableRow 
                      key={userCard.uid || userCard.id || index}
                      sx={{ 
                        '&:hover': { backgroundColor: colors.primary[500] },
                        backgroundColor: isSelected 
                          ? colors.greenAccent[800] 
                          : index % 2 === 0 ? colors.primary[400] : colors.primary[450],
                        // Visual indication for card status
                        border: isOnMarket ? `2px solid ${colors.blueAccent[500]}` : 
                               !tradeable ? `2px solid ${colors.redAccent[500]}` : 'none'
                      }}
                    >
                      <TableCell>
                        <Checkbox
                          checked={isSelected}
                          onChange={(e) => handleCardSelect(userCard.uid, e.target.checked, index, e)}
                          disabled={!canList}
                          icon={<CheckBoxOutlineBlankIcon />}
                          checkedIcon={<CheckBoxIcon />}
                          sx={{
                            color: colors.grey[100],
                            '&.Mui-checked': {
                              color: isOnMarket ? colors.blueAccent[600] : 
                                     !tradeable ? colors.redAccent[600] :
                                     colors.greenAccent[400],
                            },
                            '&.Mui-disabled': {
                              color: colors.grey[600],
                            }
                          }}
                        />
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={`Level ${userCard.level || 1}`}
                          size="small"
                          sx={{
                            backgroundColor: colors.greenAccent[600],
                            color: colors.grey[100],
                            fontWeight: 'bold'
                          }}
                        />
                      </TableCell>
                      <TableCell>
                        <Typography fontWeight="medium" color={colors.grey[100]}>
                          {Number(userCard.bcx || 1).toLocaleString('en-US')}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography fontWeight="medium" color={colors.grey[100]}>
                          {Number(userCard.collection_power || userCard.cp || 0).toLocaleString('en-US')}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Box display="flex" gap={1} flexWrap="wrap">
                          {userCard.gold && (
                            <Chip 
                              label="Gold" 
                              size="small"
                              sx={{ 
                                backgroundColor: '#DAA520', 
                                color: 'white',
                                fontWeight: 'bold'
                              }} 
                            />
                          )}
                          {userCard.foil > 0 && (
                            <Chip 
                              label={getFoilTypeName(userCard.foil)} 
                              size="small"
                              sx={{ 
                                backgroundColor: colors.blueAccent[600], 
                                color: colors.grey[100],
                                fontWeight: 'bold'
                              }} 
                            />
                          )}
                          {!userCard.gold && userCard.foil === 0 && (
                            <Chip 
                              label="Regular" 
                              size="small"
                              sx={{ 
                                backgroundColor: colors.grey[600], 
                                color: colors.grey[100] 
                              }} 
                            />
                          )}
                        </Box>
                      </TableCell>
                      <TableCell>
                        {(() => {
                          const onMarket = isCardOnMarket(userCard);
                          const tradeable = isCardTradeable(userCard);
                          const restrictions = getCardRestrictions(userCard);
                          
                          if (onMarket) {
                            return (
                              <Chip 
                                label="Listed"
                                size="small"
                                sx={{
                                  backgroundColor: colors.blueAccent[600],
                                  color: colors.grey[100]
                                }}
                              />
                            );
                          } else if (tradeable) {
                            return (
                              <Chip 
                                label="Listable"
                                size="small"
                                sx={{
                                  backgroundColor: colors.greenAccent[600],
                                  color: colors.grey[100]
                                }}
                              />
                            );
                          } else {
                            const primaryRestriction = restrictions[0] || 'Restricted';
                            return (
                              <Chip 
                                label={primaryRestriction}
                                size="small"
                                title={restrictions.join(', ')}
                                sx={{
                                  backgroundColor: colors.redAccent[600],
                                  color: colors.grey[100]
                                }}
                              />
                            );
                          }
                        })()}
                      </TableCell>
                      <TableCell>
                        {isOnMarket && userCard.buy_price ? (
                          <Box display="flex" flexDirection="column" alignItems="flex-start">
                            <Typography 
                              fontWeight="bold" 
                              color={colors.greenAccent[400]}
                              variant="body2"
                            >
                              ${Number(userCard.buy_price).toFixed(3)}
                            </Typography>
                            <Typography 
                              variant="caption" 
                              color={colors.grey[400]}
                            >
                              DEC
                            </Typography>
                          </Box>
                        ) : (
                          <Typography 
                            variant="body2" 
                            color={colors.grey[500]}
                            fontStyle="italic"
                          >
                            Not listed
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell>
                        <Tooltip title="Unique identifier for this card">
                          <Typography 
                            variant="body2" 
                            sx={{ 
                              fontFamily: 'monospace',
                              fontSize: '0.75rem',
                              color: colors.grey[400],
                              cursor: 'pointer'
                            }}
                          >
                            {userCard.uid || userCard.id || 'N/A'}
                          </Typography>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        </Grid>
      </Grid>
      </Box>

  );
};

export default MyCardsTab;
